/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable @typescript-eslint/no-explicit-any */

import type { HydraErrorType } from '@/lib/services/Hydra/_hydraError'
import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import { toast } from 'sonner'
import { getLocaleCookie } from './cookies/getLocaleCookie'
import { logError } from './log'

const createQueryString = ({
  queryParams,
}: {
  queryParams: Record<string, string>
}) => {
  let queryString = ''
  for (const [key, value] of Object.entries(queryParams)) {
    if (queryString) {
      queryString += `&${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    } else {
      queryString += `?${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    }
  }
  return queryString
}

const createErrorResponse = <T = any>(
  code: number | string,
  message: string,
  errorMessage?: string,
): T & HydraErrorType =>
  ({
    error: {
      code,
      message,
      error_message: errorMessage ?? `${code}: ${message}`,
    },
  }) as T & HydraErrorType

const getEndpointName = (url: string, method: string): string => {
  try {
    const cleanUrl = url.split('?')[0]

    const pathOnly = cleanUrl.replace(/^https?:\/\/[^/]+/, '')

    const endpointName = `${method.toUpperCase()} ${pathOnly}`

    return endpointName
  } catch {
    return `${method.toUpperCase()} ${url}`
  }
}

const logServerError = (
  error: any,
  requestInfo: {
    method: string
    url: string
    endpointName: string
    status?: number
    timeout?: number
    responseTime: number
    isTimeout?: boolean
    requestId?: string | null
    loggingParameters?: Record<string, any>
  },
) => {
  const baseErrorDetails = {
    timestamp: new Date().toISOString(),
    method: requestInfo.method,
    url: requestInfo.url,
    endpoint_name: requestInfo.endpointName,
    status: requestInfo.status || 'unknown',
    timeout_ms: requestInfo.timeout,
    response_time_ms: requestInfo.responseTime,
    is_timeout: requestInfo.isTimeout || false,
    request_id: requestInfo.requestId,
    error: error instanceof Error ? error.message : String(error),
    userAgent:
      typeof window !== 'undefined' ? window.navigator.userAgent : 'unknown',
    success: false,
  }

  const errorDetails = {
    ...baseErrorDetails,
    ...requestInfo.loggingParameters,
  }

  datadogLogs.logger.log('HTTP Server Error', errorDetails)
}

const logSuccessfulRequest = (requestInfo: {
  method: string
  url: string
  endpointName: string
  status: number
  responseTime: number
  timeout: number
  requestId?: string | null
  loggingParameters?: Record<string, any>
}) => {
  const baseLogData = {
    timestamp: new Date().toISOString(),
    method: requestInfo.method,
    url: requestInfo.url,
    endpoint_name: requestInfo.endpointName,
    status: requestInfo.status,
    response_time_ms: requestInfo.responseTime,
    timeout_ms: requestInfo.timeout,
    request_id: requestInfo.requestId,
    success: true,
  }

  const logData = {
    ...baseLogData,
    ...requestInfo.loggingParameters,
  }

  if (datadogLogs) {
    datadogLogs.logger.info('HTTP Request Success', logData)
  }

  if (datadogRum !== undefined) {
    datadogRum.addAction('http_request_success', logData)

    datadogRum.addTiming('http.response_time', requestInfo.responseTime)

    datadogRum.addAction('http_response_timing', {
      endpoint_name: requestInfo.endpointName,
      method: requestInfo.method,
      response_time_ms: requestInfo.responseTime,
      ...requestInfo.loggingParameters,
    })
  }
}

const DEFAULT_TIMEOUTS = {
  DEFAULT: 180_000,
  ORDER_SUBMISSION: 600_000,
} as const

export type SendRequestType = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'HEAD'
  baseURL?: string
  queryParams?: Record<string, string>
  url: string
  data?: any
  token?: string
  withAuth?: boolean
  headers?: Record<string, string>
  returnType?: string
  withErrorHandling?: boolean
  timeout?: number
  returnStructuredErrors?: boolean
  loggingParameters?: Record<string, any>
  loggingEnabled?: boolean
}

export type HttpToolsType = {
  getCdnUrl: () => string
  getHeaders: ({
    withAuth,
    token,
    headers,
    isHydra,
    isCosmic,
  }: {
    withAuth: boolean
    token: string
    headers: Record<string, string>
    isHydra: boolean
    isCosmic: boolean
  }) => Record<string, string>
  sendRequest: <T = HydraErrorType>({
    method,
    baseURL,
    queryParams,
    url,
    data,
    token,
    withAuth,
    headers,
    returnType,
    withErrorHandling,
    timeout,
    returnStructuredErrors,
    loggingParameters,
  }: SendRequestType) => Promise<(T & HydraErrorType) | null>
  DEFAULT_TIMEOUTS: typeof DEFAULT_TIMEOUTS
}

const createTimeoutController = (timeoutMs: number): AbortController => {
  const controller = new AbortController()

  setTimeout(() => {
    controller.abort()
  }, timeoutMs)

  return controller
}

const httpTools: HttpToolsType = {
  getCdnUrl: () => 'https://cdn.unicity.com/config',
  DEFAULT_TIMEOUTS,
  getHeaders: ({
    headers,
    withAuth,
    token,
    isHydra,
    isCosmic,
  }): Record<string, string> => {
    const jsonHeaders: Record<string, string> = {
      'Content-Type': 'application/json; charset=utf-8',
    }

    if (withAuth) {
      jsonHeaders.Authorization = `Bearer ${token}`
    }

    if (isHydra || isCosmic) {
      jsonHeaders['x-application'] = 'Shop2.0'
    }

    const locale: string = headers['Accept-Language'] ?? getLocaleCookie()

    return {
      ...headers,
      ...jsonHeaders,
      'Accept-Language': locale,
    }
  },
  sendRequest: async <T = any>({
    method = 'GET',
    baseURL = process.env.NEXT_PUBLIC_HYDRA_BASE_URL,
    queryParams = {},
    url = '',
    data = null,
    token = '',
    withAuth = true,
    headers = {},
    returnType = 'json',
    withErrorHandling = true,
    timeout,
    returnStructuredErrors = false,
    loggingParameters = {},
    loggingEnabled = true, // Errors are always logged, this only affects success api calls logging
  }: SendRequestType): Promise<T & HydraErrorType> => {
    type ResponseType = T & HydraErrorType

    const requestTimeout = timeout ?? DEFAULT_TIMEOUTS.DEFAULT

    const startTime = performance.now()

    const endpointName = getEndpointName(url, method)

    try {
      let correctBaseURL = baseURL === 'internal' ? '' : baseURL

      correctBaseURL =
        correctBaseURL === 'jeeves'
          ? process.env.NEXT_PUBLIC_JEEVES_BASE_URL ?? ''
          : correctBaseURL

      const isHydra =
        correctBaseURL?.includes(
          process.env.NEXT_PUBLIC_HYDRA_BASE_URL || 'hydra',
        ) || false

      const isCosmic =
        correctBaseURL?.includes(
          process.env.NEXT_PUBLIC_COSMIC_BASE_URL || 'solar.unicity.com',
        ) || false

      let query = ''

      if (queryParams) {
        query = createQueryString({ queryParams })
      }

      const requestUrl = `${correctBaseURL}${url}${query}`

      const requestData = JSON.stringify(data)

      const controller = createTimeoutController(requestTimeout)

      const response = await fetch(requestUrl, {
        method,
        headers: httpTools.getHeaders({
          headers,
          withAuth,
          token,
          isHydra,
          isCosmic,
        }),
        signal: controller.signal,
        ...(requestData &&
          (method === 'POST' || method === 'PUT') && { body: requestData }),
      })

      const endTime = performance.now()
      const responseTime = endTime - startTime
      const requestId = response.headers.get('x-request-id')

      const currentUrl = new URL(window.location.href)
      const pathnameAllSegments = currentUrl.pathname.split('/')
      const pathnameNeededSegments = pathnameAllSegments.slice(0, 3)
      const portUrl = currentUrl.port ? `:${currentUrl.port}` : ''

      const redirectToInvalidUrl = `${currentUrl.protocol}//${
        currentUrl.hostname
      }${portUrl}${pathnameNeededSegments.join('/')}/session-expired`

      if (returnType === 'json') {
        let responseData: ResponseType

        if (response.ok) {
          responseData = (await response.json()) as ResponseType

          if (loggingEnabled) {
            logSuccessfulRequest({
              method,
              url: requestUrl,
              endpointName,
              status: response.status,
              responseTime,
              timeout: requestTimeout,
              requestId,
              loggingParameters,
            })
          }
        } else {
          try {
            const jsonResponse = (await response.json()) as {
              error?: {
                code?: number | string
                message?: string
                error_message?: string
              }
              code?: number | string
              message?: string
              error_message?: string
            }

            logServerError(
              new Error(
                `HTTP ${response.status}: ${jsonResponse.error?.message}`,
              ),
              {
                method,
                url: requestUrl,
                endpointName,
                status: response.status,
                timeout: requestTimeout,
                responseTime,
                isTimeout: false,
                requestId,
                loggingParameters,
              },
            )

            if (jsonResponse.error) {
              responseData = jsonResponse as ResponseType
            } else {
              responseData = createErrorResponse<T>(
                jsonResponse.code ?? response.status,
                jsonResponse.message ?? response.statusText,
                jsonResponse.error_message ??
                  `HTTP ${response.status}: ${response.statusText}`,
              )
            }
          } catch {
            responseData = createErrorResponse<T>(
              response.status,
              response.statusText,
              `HTTP ${response.status}: ${response.statusText}`,
            )
          }
        }

        if (withErrorHandling && responseData.error) {
          toast.error(
            `${responseData.error.code}: ${responseData.error.message}`,
          )
        }

        if (responseData.error?.error_message === 'Invalid Bearer Token.') {
          window.location.href = redirectToInvalidUrl
          return null as unknown as ResponseType
        }

        return responseData
      }

      if (returnType === 'response') {
        const responseWithTypes = response as unknown as ResponseType

        if (response.ok && loggingEnabled) {
          logSuccessfulRequest({
            method,
            url: requestUrl,
            endpointName,
            status: response.status,
            responseTime: endTime - startTime,
            timeout: requestTimeout,
            requestId,
            loggingParameters,
          })
        }

        if (withErrorHandling && responseWithTypes.error) {
          toast.error(
            `${responseWithTypes.error.code}: ${responseWithTypes.error.message}`,
          )
        }

        if (
          responseWithTypes.error?.error_message === 'Invalid Bearer Token.'
        ) {
          window.location.href = redirectToInvalidUrl
          return null as unknown as ResponseType
        }

        return responseWithTypes
      }

      if (returnType === 'blob') {
        if (loggingEnabled) {
          logSuccessfulRequest({
            method,
            url: requestUrl,
            endpointName,
            status: response.status,
            responseTime: endTime - startTime,
            timeout: requestTimeout,
            requestId,
            loggingParameters,
          })
        }

        return response.blob() as unknown as ResponseType
      }

      if (returnStructuredErrors) {
        return createErrorResponse<T>(
          400,
          'Unsupported return type',
          `Unsupported return type: ${returnType}. Supported types are: json, response, blob`,
        )
      }
      return null as unknown as ResponseType
    } catch (error) {
      const endTime = performance.now()
      const responseTime = endTime - startTime
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error'
      const isTimeout = error instanceof Error && error.name === 'AbortError'

      logError(`HTTP Error: ${errorMessage}`)

      if (isTimeout) {
        logServerError(error, {
          method,
          url,
          endpointName,
          timeout: requestTimeout,
          responseTime,
          isTimeout: true,
          requestId: null,
          loggingParameters,
        })

        if (withErrorHandling) {
          toast.error(
            `Request timed out after ${requestTimeout / 1000} seconds`,
          )
        }

        // Return a timeout error in the expected format or null based on flag
        if (returnStructuredErrors) {
          return createErrorResponse<T>(
            408,
            'Request timeout',
            `Request timed out after ${requestTimeout}ms`,
          )
        }
        return null as unknown as ResponseType
      }

      logServerError(error, {
        method,
        url,
        endpointName,
        responseTime,
        isTimeout: false,
        requestId: null,
        loggingParameters,
      })

      if (withErrorHandling) {
        toast.error(`Network error: ${errorMessage}`)
      }

      if (returnStructuredErrors) {
        return createErrorResponse<T>(
          500,
          'Network error',
          `Network request failed: ${errorMessage}`,
        )
      }
      return null as unknown as ResponseType
    }
  },
}

export default httpTools
